export const DOCUMENT_SURFACE_POLICY_SCHEMA_VERSION =
  "cogo.studio-document-surface-policy.v1" as const;

export const CHART_INTEGRATION_PLAN_SCHEMA_VERSION =
  "cogo.studio-chart-integration-plan.v1" as const;

export type DocumentSurfaceStatus =
  | "ready"
  | "warn"
  | "blocked"
  | "missing"
  | "planned";

export type DocumentHelperSurfaceId =
  | "wordgard"
  | "univer"
  | "cogo-document-model"
  | "cogo-chart-sidecar";

export type DocumentHelperSurfaceRole =
  | "agentic-document-editor"
  | "sheet-formula-oracle"
  | "chart-preview-helper"
  | "canonical-document-authority"
  | "canonical-chart-authority";

export interface DocumentHelperSurfacePolicy {
  id: DocumentHelperSurfaceId;
  label: string;
  role: DocumentHelperSurfaceRole;
  status: DocumentSurfaceStatus;
  integrationMode:
    | "canonical"
    | "optional-adapter"
    | "lazy-plugin-headless"
    | "planned";
  authority:
    | "cogo-owned-canonical-model"
    | "helper-surface-only"
    | "proof-oracle-only";
  ownsCanonicalModel: boolean;
  mayPatchCogoJsonDirectly: boolean;
  allowedUses: string[];
  prohibitedUses: string[];
  evidenceRefs: string[];
}

export interface StudioDocumentSurfacePolicy {
  schemaVersion: typeof DOCUMENT_SURFACE_POLICY_SCHEMA_VERSION;
  status: DocumentSurfaceStatus;
  surfaces: DocumentHelperSurfacePolicy[];
  blockers: string[];
}

export interface ChartIntegrationStage {
  id: "P0" | "P1" | "P2" | "P3" | "P4";
  title: string;
  status: DocumentSurfaceStatus;
  deliverable: string;
  ownerSurface: DocumentHelperSurfaceId;
}

export interface StudioChartIntegrationPlan {
  schemaVersion: typeof CHART_INTEGRATION_PLAN_SCHEMA_VERSION;
  status: DocumentSurfaceStatus;
  canonicalAuthority: "cogo-chart-sidecar";
  helperSurfaces: Array<"univer" | "wordgard">;
  stages: ChartIntegrationStage[];
  blockers: string[];
  sourceLocations: string[];
}

const BASE_SURFACE_POLICIES: DocumentHelperSurfacePolicy[] = [
  {
    id: "cogo-document-model",
    label: "Coogo Document Model",
    role: "canonical-document-authority",
    status: "ready",
    integrationMode: "canonical",
    authority: "cogo-owned-canonical-model",
    ownsCanonicalModel: true,
    mayPatchCogoJsonDirectly: false,
    allowedUses: [
      "report structure authority",
      "semantic patch target",
      "export readiness source",
    ],
    prohibitedUses: ["opaque editor state as source of truth"],
    evidenceRefs: ["packages/cogo-schema/v4/document-model-ref.cogo.schema.json"],
  },
  {
    id: "cogo-chart-sidecar",
    label: "Coogo Chart Data",
    role: "canonical-chart-authority",
    status: "warn",
    integrationMode: "canonical",
    authority: "cogo-owned-canonical-model",
    ownsCanonicalModel: true,
    mayPatchCogoJsonDirectly: false,
    allowedUses: [
      "chart semantic intent",
      "series binding",
      "native export proof",
      "ActionFlow chart data validation",
    ],
    prohibitedUses: ["renderer-specific pixel tuning as semantic authority"],
    evidenceRefs: [
      "docs/agentic/REPORT-XLSX-FORMULA-ORACLE-PHASE2-SEMANTIC-COVERAGE-2026-07-05.md",
    ],
  },
  {
    id: "wordgard",
    label: "WordGard",
    role: "agentic-document-editor",
    status: "warn",
    integrationMode: "optional-adapter",
    authority: "helper-surface-only",
    ownsCanonicalModel: false,
    mayPatchCogoJsonDirectly: false,
    allowedUses: [
      "proposal-first narrative edits",
      "agentic document comments and requirements history",
      "human-approved semantic patch proposals",
      "long-form draft review while Cogo retains layout and style authority",
    ],
    prohibitedUses: [
      "canonical Coogo JSON storage",
      "export proof authority",
      "native DOCX/PPTX/HWPX writer replacement",
    ],
    evidenceRefs: [
      "apps/cogo-studio/src/features/document-workspace/wordgard-page-editor.tsx",
      "apps/cogo-studio/src/features/document-workspace/wordgard-document-operation-bridge.ts",
    ],
  },
  {
    id: "univer",
    label: "Univer",
    role: "sheet-formula-oracle",
    status: "warn",
    integrationMode: "lazy-plugin-headless",
    authority: "proof-oracle-only",
    ownsCanonicalModel: false,
    mayPatchCogoJsonDirectly: false,
    allowedUses: [
      "sheet formula oracle",
      "sheet chart preview helper",
      "workbook dependency inspection",
      "ActionFlow calculation checkpoint comparison",
    ],
    prohibitedUses: [
      "canonical chart schema authority",
      "report export writer replacement",
      "direct Coogo JSON patching",
    ],
    evidenceRefs: [
      "apps/cogo-studio/src/features/report-workspace/univer-formula-oracle.ts",
    ],
  },
];

export function buildStudioDocumentSurfacePolicy(input?: {
  wordgardReviewed?: boolean;
  chartSidecarReady?: boolean;
  univerFormulaOracleReady?: boolean;
}): StudioDocumentSurfacePolicy {
  const surfaces = BASE_SURFACE_POLICIES.map((surface) => {
    if (surface.id === "wordgard" && input?.wordgardReviewed) {
      return { ...surface, status: "warn" as const };
    }
    if (surface.id === "cogo-chart-sidecar" && input?.chartSidecarReady) {
      return { ...surface, status: "ready" as const };
    }
    if (surface.id === "univer" && input?.univerFormulaOracleReady) {
      return { ...surface, status: "ready" as const };
    }
    return surface;
  });

  const blockers = surfaces.flatMap((surface) => {
    if (surface.ownsCanonicalModel || surface.status !== "planned") return [];
    return [`${surface.id}: integration plan is not reviewed yet`];
  });

  return {
    schemaVersion: DOCUMENT_SURFACE_POLICY_SCHEMA_VERSION,
    status:
      blockers.length > 0 ||
      surfaces.some(
        (surface) => surface.status === "warn" || surface.status === "planned",
      )
        ? "warn"
        : "ready",
    surfaces,
    blockers,
  };
}

export function buildStudioChartIntegrationPlan(input?: {
  chartSidecarReady?: boolean;
  univerChartAdapterReady?: boolean;
  reportWorkspaceChartInspectorReady?: boolean;
  exportProofLinked?: boolean;
}): StudioChartIntegrationPlan {
  const stages: ChartIntegrationStage[] = [
    {
      id: "P0",
      title: "Authority boundary",
      status: "ready",
      deliverable:
        "Keep Coogo chart data as the single source of truth; keep helpers non-authoritative.",
      ownerSurface: "cogo-chart-sidecar",
    },
    {
      id: "P1",
      title: "Chart data model",
      status: input?.chartSidecarReady ? "ready" : "warn",
      deliverable:
        "Normalize XLSX/DOCX/PPTX/HWPX chart intent, series, ranges, titles, and style tokens.",
      ownerSurface: "cogo-chart-sidecar",
    },
    {
      id: "P2",
      title: "Univer chart adapter",
      status: input?.univerChartAdapterReady ? "ready" : "planned",
      deliverable:
        "Use Univer only to inspect workbook chart state and preview sheet-backed charts.",
      ownerSurface: "univer",
    },
    {
      id: "P3",
      title: "Report Workspace chart inspector",
      status: input?.reportWorkspaceChartInspectorReady ? "ready" : "planned",
      deliverable:
        "Expose chart data, formula oracle, ActionFlow checkpoint, and native export verification evidence together.",
      ownerSurface: "cogo-document-model",
    },
    {
      id: "P4",
      title: "Export verification evidence linkage",
      status: input?.exportProofLinked ? "ready" : "planned",
      deliverable:
        "Connect chart verification evidence to PDF/DOCX/PPTX/HWPX file checks and visual evidence.",
      ownerSurface: "cogo-chart-sidecar",
    },
  ];

  const blockers = stages.flatMap((stage) =>
    stage.status === "ready" ? [] : [`${stage.id}: ${stage.title}`],
  );

  return {
    schemaVersion: CHART_INTEGRATION_PLAN_SCHEMA_VERSION,
    status: blockers.length > 0 ? "warn" : "ready",
    canonicalAuthority: "cogo-chart-sidecar",
    helperSurfaces: ["univer", "wordgard"],
    stages,
    blockers,
    sourceLocations: [
      "apps/cogo-studio/src/features/report-workspace/univer-formula-oracle.ts",
      "scripts/agentic/extract-xlsx-formula-oracle.ts",
      "scripts/agentic/check-sheet-formula-actionflow-oracle.ts",
      "docs/studio/wordgard-univer-chart-integration-plan-2026-07-05.md",
    ],
  };
}

export function findDocumentHelperSurfacePolicy(
  id: DocumentHelperSurfaceId,
  policy = buildStudioDocumentSurfacePolicy(),
): DocumentHelperSurfacePolicy | undefined {
  return policy.surfaces.find((surface) => surface.id === id);
}
